import React, { useState, useEffect, useMemo } from 'react';
import { Users, UserCheck, Shield, Tag, ChevronDown, ChevronUp, X, Loader2 } from 'lucide-react';
import api from '../../utils/api';

// Cache the available placeholders so we don't re-fetch on every expand
let placeholderCache = null;
let cacheTimestamp = 0;
const CACHE_TTL = 60000; // 1 minute

const CATEGORY_META = {
    relationship: { label: 'Relationships', icon: UserCheck, color: 'bg-blue-50 text-blue-700 border-blue-200', chipColor: 'bg-blue-100 text-blue-700' },
    team: { label: 'Team', icon: Users, color: 'bg-green-50 text-green-700 border-green-200', chipColor: 'bg-green-100 text-green-700' },
    company: { label: 'Company-Wide', icon: Users, color: 'bg-orange-50 text-orange-700 border-orange-200', chipColor: 'bg-orange-100 text-orange-700' },
    role: { label: 'Roles', icon: Shield, color: 'bg-purple-50 text-purple-700 border-purple-200', chipColor: 'bg-purple-100 text-purple-700' },
    custom: { label: 'Custom', icon: Tag, color: 'bg-teal-50 text-teal-700 border-teal-200', chipColor: 'bg-teal-100 text-teal-700' },
};

const AssignmentPlaceholderPicker = ({ value = [], onChange }) => {
    const [allPlaceholders, setAllPlaceholders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        const fetchPlaceholders = async () => {
            // Use cache if fresh
            if (placeholderCache && Date.now() - cacheTimestamp < CACHE_TTL) {
                setAllPlaceholders(placeholderCache);
                return;
            }

            setLoading(true);
            try {
                const res = await api.get('/placeholders/available');
                if (res.data?.success) {
                    placeholderCache = res.data.assignment_placeholders || [];
                    cacheTimestamp = Date.now();
                    setAllPlaceholders(placeholderCache);
                }
            } catch (err) {
                console.error('Failed to fetch placeholders:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchPlaceholders();
    }, []);

    // Group placeholders by category
    const grouped = useMemo(() => {
        const groups = {};
        for (const p of allPlaceholders) {
            const cat = p.category || 'custom';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(p);
        }
        return groups;
    }, [allPlaceholders]);

    const selectedSet = useMemo(() => new Set(value), [value]);

    const toggle = (slug) => {
        const next = selectedSet.has(slug)
            ? value.filter(v => v !== slug)
            : [...value, slug];
        onChange(next);
    };

    const remove = (slug) => {
        onChange(value.filter(v => v !== slug));
    };

    // Resolve display info for a slug
    const getDisplayInfo = (slug) => {
        const found = allPlaceholders.find(p => p.slug === slug);
        if (found) {
            const meta = CATEGORY_META[found.category] || CATEGORY_META.custom;
            return { name: found.name, chipColor: meta.chipColor };
        }
        return { name: slug, chipColor: 'bg-gray-100 text-gray-700' };
    };

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <label className="block text-xs font-medium text-gray-600 flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" />
                    Assign To (Placeholders)
                </label>
                <button
                    type="button"
                    onClick={() => setExpanded(!expanded)}
                    className="text-xs text-violet-600 hover:text-violet-800 font-medium flex items-center gap-1"
                >
                    {expanded ? 'Hide' : 'Configure'}
                    {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
            </div>

            {/* Selected chips */}
            {value.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {value.map(slug => {
                        const { name, chipColor } = getDisplayInfo(slug);
                        return (
                            <span
                                key={slug}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${chipColor}`}
                            >
                                {name}
                                <button
                                    type="button"
                                    onClick={() => remove(slug)}
                                    className="hover:bg-black/10 rounded-full p-0.5"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        );
                    })}
                </div>
            )}

            {value.length === 0 && !expanded && (
                <p className="text-xs text-gray-400 italic">No placeholders assigned — this item will be assigned to the employee directly</p>
            )}

            {/* Placeholder picker panel */}
            {expanded && (
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                    {loading ? (
                        <div className="p-4 flex items-center justify-center gap-2 text-gray-400">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-xs">Loading placeholders...</span>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {Object.entries(grouped).map(([category, items]) => {
                                const meta = CATEGORY_META[category] || CATEGORY_META.custom;
                                const Icon = meta.icon;
                                return (
                                    <div key={category} className="p-2.5">
                                        <div className="flex items-center gap-1.5 mb-2">
                                            <Icon className="w-3.5 h-3.5 text-gray-500" />
                                            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                                                {meta.label}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {items.map(p => {
                                                const isSelected = selectedSet.has(p.slug);
                                                return (
                                                    <button
                                                        key={p.slug}
                                                        type="button"
                                                        onClick={() => toggle(p.slug)}
                                                        title={p.description}
                                                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${isSelected
                                                                ? `${meta.color} ring-2 ring-offset-1 ring-violet-300`
                                                                : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-100'
                                                            }`}
                                                    >
                                                        {p.name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}

                            {Object.keys(grouped).length === 0 && (
                                <div className="p-4 text-center text-xs text-gray-400">
                                    No placeholders available. Create custom placeholders in Settings.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AssignmentPlaceholderPicker;
